/**
 * 계정 로그인 기록 창 — 고객 상세의 계정 칸에서 "로그인 기록" 버튼으로 연다(운영자 결정 2026-10-12).
 * ★ 최근 로그인·30일 횟수는 **본인 로그인만** 센다 — 운영자 대리 로그인은 기록 창에 따로 표시만 한다.
 * ★ 세션 행을 지우지 않으므로 운영 시작부터의 기록이 다 있다. "마지막 활동"은 몇 분 단위로 묶여 갱신된다.
 */
import { auth } from '@geo-admin/shared/contract/api/index.ts';
import type { RouteResponse } from '@geo-admin/shared/contract/route.ts';
import { useApi } from '../lib/api.ts';
import { dateTime, relative } from '../lib/format.ts';
import { Badge } from './ui/card.tsx';
import { Dialog } from './ui/dialog.tsx';
import { ErrorBox, Loading } from './ui/states.tsx';

/** 계정 목록(`listUsers`)의 행이면 최근 로그인 요약까지, 브랜드 소속 목록이면 아이디·이메일만 온다 */
type User = Pick<RouteResponse<typeof auth.listUsers>['users'][number], 'id' | 'email'> &
  Partial<Pick<RouteResponse<typeof auth.listUsers>['users'][number], 'lastLoginAt' | 'loginCount30d'>>;

const REALM_LABEL: Record<string, string> = { admin: '운영자', app: '사용자', agency: '대행사(옛 영역)' };

const LOGIN_STATE = {
  active: { label: '접속 중', tone: 'green' },
  loggedOut: { label: '로그아웃', tone: 'slate' },
  expired: { label: '만료', tone: 'slate' },
} as const;

/**
 * 한 계정의 로그인 기록 — 로그인 한 번이 한 줄(최근 100건). 운영자 대리 로그인은 따로 표시한다.
 * ★ 세션 행을 지우지 않으므로 운영 시작부터의 기록이 다 있다. "마지막 활동"은 몇 분 단위로 묶여 갱신된다.
 */
export function LoginHistoryDialog({ user, onClose }: { user: User | null; onClose: () => void }) {
  const logins = useApi(auth.listUserLogins, { params: { id: user?.id ?? '' } }, { enabled: user !== null });
  const list = logins.data?.logins ?? [];
  const own = list.filter((l) => !l.impersonatedBy);
  return (
    <Dialog open={user !== null} onClose={onClose} title={`로그인 기록 — ${user?.email ?? ''}`}>
      {logins.isLoading ? (
        <Loading />
      ) : logins.error ? (
        <ErrorBox error={logins.error} />
      ) : list.length === 0 ? (
        <p className="py-6 text-center text-slate-500 text-sm">로그인한 적이 없습니다</p>
      ) : (
        <div className="grid gap-3">
          <p className="text-slate-500 text-sm">
            {user?.lastLoginAt ? (
              <>
                최근 로그인 <b className="text-slate-900">{relative(user.lastLoginAt)}</b>({dateTime(user.lastLoginAt)})
                · 30일 {user.loginCount30d}회 ·{' '}
              </>
            ) : null}
            본인 로그인 {own.length}회{list.length > own.length ? ` · 운영자 대리 ${list.length - own.length}회` : ''}
            {list.length >= 100 ? ' (최근 100건)' : ''}
          </p>
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white text-left text-slate-400 text-xs">
                <tr>
                  <th className="py-1.5 pr-3 font-medium">로그인</th>
                  <th className="py-1.5 pr-3 font-medium">마지막 활동</th>
                  <th className="py-1.5 pr-3 font-medium">화면</th>
                  <th className="py-1.5 font-medium">상태</th>
                </tr>
              </thead>
              <tbody>
                {list.map((l) => (
                  <tr key={l.at} className="border-slate-100 border-t">
                    <td className="py-1.5 pr-3 tabular-nums">{dateTime(l.at)}</td>
                    <td className="py-1.5 pr-3 text-slate-500 tabular-nums">{dateTime(l.lastSeenAt)}</td>
                    <td className="py-1.5 pr-3 text-slate-500">{l.realm ? (REALM_LABEL[l.realm] ?? l.realm) : '—'}</td>
                    <td className="py-1.5">
                      <span className="flex flex-wrap items-center gap-1">
                        <Badge tone={LOGIN_STATE[l.state].tone}>{LOGIN_STATE[l.state].label}</Badge>
                        {l.impersonatedBy ? (
                          <Badge tone="amber" title="운영자가 이 계정으로 대리 로그인했다">
                            대리 · {l.impersonatedBy}
                          </Badge>
                        ) : null}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Dialog>
  );
}
